import { Injectable } from '@angular/core';
import { Observable, defer, of, timer } from 'rxjs';
import { catchError, exhaustMap, filter, map } from 'rxjs/operators';
import { ResponseAPI } from '../models/HttpBase/response-api';
import {
  PremiacionEventoItem,
  PremiacionEventosPollBatch,
  PremiacionEventosPollOptions
} from '../models/exposicion/premiacion-evento.dto';
import { HttpClientService } from './http-client.service';

@Injectable({
  providedIn: 'root'
})
export class PremiacionEventosPollingService {
  private readonly defaultIntervalMs = 3500;
  private readonly defaultTop = 80;

  constructor(private readonly httpClientService: HttpClientService) {}

  public watch(
    exposicionId: number,
    options: PremiacionEventosPollOptions = {}
  ): Observable<PremiacionEventosPollBatch> {
    const intervalMs = this.resolvePositiveInt(options.intervalMs, this.defaultIntervalMs);
    const top = this.resolvePositiveInt(options.top, this.defaultTop);
    const hasExplicitInitialEventoId =
      options.initialEventoId !== undefined && options.initialEventoId !== null;
    const initialEventoId = hasExplicitInitialEventoId
      ? this.resolvePositiveInt(options.initialEventoId, 0)
      : 0;

    return defer(() => {
      let lastEventoId = initialEventoId;
      // Sin initialEventoId explicito, hacemos "priming":
      // la primera lectura solo fija el ultimo EventoID para evitar reinyectar historial.
      let isPriming = !hasExplicitInitialEventoId;

      return timer(0, intervalMs).pipe(
        exhaustMap(() =>
          this.fetchEventos(exposicionId, lastEventoId, top).pipe(
            catchError(() =>
              of({
                desdeEventoId: lastEventoId,
                ultimoEventoId: lastEventoId,
                eventos: [] as PremiacionEventoItem[]
              })
            )
          )
        ),
        map((payload) => {
          const previousLast = lastEventoId;
          const nextLast = Math.max(previousLast, payload.ultimoEventoId);
          lastEventoId = nextLast;

          if (isPriming) {
            isPriming = false;
            return {
              previousLast,
              nextLast,
              eventos: [] as PremiacionEventoItem[]
            };
          }

          const eventos = payload.eventos.filter((evento) => this.resolveEventoId(evento) > previousLast);
          return {
            previousLast,
            nextLast,
            eventos
          };
        }),
        filter((payload) => payload.eventos.length > 0 && payload.nextLast > payload.previousLast),
        map((payload) => ({
          exposicionId,
          desdeEventoId: payload.previousLast,
          ultimoEventoId: payload.nextLast,
          eventos: payload.eventos
        }))
      );
    });
  }

  private fetchEventos(
    exposicionId: number,
    desdeEventoId: number,
    top: number
  ): Observable<{ desdeEventoId: number; ultimoEventoId: number; eventos: PremiacionEventoItem[] }> {
    const query = `desdeEventoID=${encodeURIComponent(String(Math.max(0, desdeEventoId)))}&top=${encodeURIComponent(
      String(Math.max(1, top))
    )}`;
    const path = `Premios/premiacionEventos/${exposicionId}?${query}`;

    return this.httpClientService
      .getSilent<ResponseAPI<PremiacionEventoItem[] | { Eventos?: PremiacionEventoItem[]; eventos?: PremiacionEventoItem[] }>>(path)
      .pipe(
        map((rawResponse) =>
          this.hydrateResponse<PremiacionEventoItem[] | { Eventos?: PremiacionEventoItem[]; eventos?: PremiacionEventoItem[] }>(
            rawResponse
          )
        ),
        map((response) => {
          if (!response.Success) {
            return {
              desdeEventoId,
              ultimoEventoId: desdeEventoId,
              eventos: [] as PremiacionEventoItem[]
            };
          }

          const eventos = this.extractEventos(response.ResponseObject)
            .filter((evento) => this.resolveEventoId(evento) > desdeEventoId)
            .sort((a, b) => this.resolveEventoId(a) - this.resolveEventoId(b));
          const ultimoEventoId = eventos.reduce(
            (acc, evento) => Math.max(acc, this.resolveEventoId(evento)),
            desdeEventoId
          );

          return {
            desdeEventoId,
            ultimoEventoId,
            eventos
          };
        })
      );
  }

  private extractEventos(
    source: PremiacionEventoItem[] | { Eventos?: PremiacionEventoItem[]; eventos?: PremiacionEventoItem[] } | null
  ): PremiacionEventoItem[] {
    if (!source) {
      return [];
    }

    if (Array.isArray(source)) {
      return source;
    }

    if (Array.isArray(source.Eventos)) {
      return source.Eventos;
    }

    if (Array.isArray(source.eventos)) {
      return source.eventos;
    }

    return [];
  }

  private resolveEventoId(evento: PremiacionEventoItem): number {
    return this.resolvePositiveInt(evento.EventoID ?? evento.eventId, 0);
  }

  private resolvePositiveInt(value: unknown, fallback: number): number {
    const numeric = Number(value);
    if (!Number.isFinite(numeric) || numeric < 0) {
      return fallback;
    }
    return Math.trunc(numeric);
  }

  private hydrateResponse<T>(response: unknown): ResponseAPI<T> {
    return Object.assign(new ResponseAPI<T>(), response);
  }
}
